/**
 * Link Graph — concept mock seed data.
 *
 * Five SOPs scattered across four different DMS silos (Veeva Vault,
 * SharePoint, Documentum, TrackWise). They reference each other, and the
 * references carry planted risk states so the demo tells a story: your
 * corpus isn't broken because AudRI audited a system — it's broken
 * because your DMSes don't talk to each other.
 *
 * This is intentionally not wired to the real /api/sops endpoint — it's
 * a concept surface for customer validation, not a production data view.
 */

export const DMS = {
    veeva: {
        id: "veeva",
        name: "Veeva Vault",
        accent: "#0E7490", // teal — Veeva's brand direction (muted to fit AudRI)
        accentBg: "#ECFEFF",
        accentBorder: "#A5F3FC",
        short: "Veeva",
    },
    sharepoint: {
        id: "sharepoint",
        name: "SharePoint",
        accent: "#1D4ED8",
        accentBg: "#DBEAFE",
        accentBorder: "#BFDBFE",
        short: "SharePoint",
    },
    documentum: {
        id: "documentum",
        name: "Documentum",
        accent: "#6D28D9",
        accentBg: "#EDE9FE",
        accentBorder: "#DDD6FE",
        short: "Documentum",
    },
    trackwise: {
        id: "trackwise",
        name: "TrackWise Digital",
        accent: "#B45309",
        accentBg: "#FEF3C7",
        accentBorder: "#FDE68A",
        short: "TrackWise",
    },
};

// Risk states for cross-SOP references
export const RISK = {
    contradicts: {
        id: "contradicts",
        label: "Contradiction",
        severity: "Critical",
        color: "#DC2626",
        lineStyle: "solid",
        description:
            "Two SOPs make rules that logically exclude each other. An inspector who reads both will flag the corpus as internally inconsistent.",
    },
    stale: {
        id: "stale",
        label: "Stale revision",
        severity: "Major",
        color: "#EA580C",
        lineStyle: "dashed",
        description:
            "The citing SOP references an older revision of this SOP. The rule that was true at citation time is no longer the current requirement.",
    },
    broken: {
        id: "broken",
        label: "Broken reference",
        severity: "Major",
        color: "#B91C1C",
        lineStyle: "dotted",
        description:
            "The cited SOP no longer exists (deprecated / withdrawn). The link cannot be resolved to any current controlled document.",
    },
    drift: {
        id: "drift",
        label: "Semantic drift",
        severity: "Minor",
        color: "#CA8A04",
        lineStyle: "dashed",
        description:
            "Both SOPs still exist and are current, but the rule wording has drifted apart over time. Meaning differs, language doesn't match.",
    },
    aligned: {
        id: "aligned",
        label: "Aligned",
        severity: "Observation",
        color: "#16A34A",
        lineStyle: "solid",
        description: "Both SOPs agree. No remediation needed.",
    },
};

export const SOPS = [
    {
        id: "SOP-CSV-001",
        title: "Computer System Validation",
        dms: "veeva",
        revision: "v7.2",
        owner: "Sarah Ramirez · QA",
        last_reviewed: "2025-11-14",
        rules: [
            {
                id: "CSV-001-R1",
                text: "All GxP systems must enforce validated user access controls (SSO + MFA).",
                criticality: "Critical",
            },
            {
                id: "CSV-001-R2",
                text: "Periodic review every 12 months, signed by QA Manager.",
                criticality: "Major",
            },
            {
                id: "CSV-001-R3",
                text: "Annual restore test with documented evidence.",
                criticality: "Major",
            },
            {
                id: "CSV-001-R4",
                text: "Change control required for ALL configuration changes, no exceptions.",
                criticality: "Critical",
            },
        ],
    },
    {
        id: "SOP-DI-004",
        title: "Data Integrity (ALCOA+)",
        dms: "sharepoint",
        revision: "v3.0",
        owner: "Marcus Okafor · Data Integrity",
        last_reviewed: "2025-09-02",
        rules: [
            {
                id: "DI-004-R1",
                text: "ALCOA+ principles apply to all GxP electronic records.",
                criticality: "Critical",
            },
            {
                id: "DI-004-R2",
                text: "User access logs retained for 7 years, reviewed quarterly.",
                criticality: "Major",
            },
            {
                id: "DI-004-R3",
                text: "Audit trail review performed QUARTERLY by system owner.",
                criticality: "Major",
            },
            {
                id: "DI-004-R4",
                text: "E-signatures must comply with 21 CFR Part 11 § 11.200.",
                criticality: "Critical",
            },
        ],
    },
    {
        id: "SOP-CC-007",
        title: "Change Control",
        dms: "documentum",
        revision: "v2.4",
        owner: "Priya Natarajan · Change Mgmt",
        last_reviewed: "2024-06-18",
        rules: [
            {
                id: "CC-007-R1",
                text: "Minor / cosmetic configuration changes exempt from full change control review.",
                criticality: "Critical",
            },
            {
                id: "CC-007-R2",
                text: "Impact assessment required for any GxP system change.",
                criticality: "Major",
            },
            {
                id: "CC-007-R3",
                text: "CAPA triggered automatically if GxP impact identified.",
                criticality: "Major",
            },
            {
                id: "CC-007-R4",
                text: "Deviation route available for unapproved emergency changes.",
                criticality: "Minor",
            },
        ],
    },
    {
        id: "SOP-AT-012",
        title: "Audit Trail Review",
        dms: "trackwise",
        revision: "v1.1",
        owner: "Daniel Voss · QA Operations",
        last_reviewed: "2023-02-11", // intentionally old
        rules: [
            {
                id: "AT-012-R1",
                text: "Audit trail review performed WEEKLY by the system owner.",
                criticality: "Major",
            },
            {
                id: "AT-012-R2",
                text: "Anomalies flagged within 48 hours.",
                criticality: "Major",
            },
            {
                id: "AT-012-R3",
                text: "QA signs off monthly on all audit trail review records.",
                criticality: "Minor",
            },
        ],
    },
    {
        id: "SOP-DR-022",
        title: "Deviation Response",
        dms: "veeva",
        revision: "v4.1",
        owner: "Elena Morales · QA",
        last_reviewed: "2025-08-30",
        rules: [
            {
                id: "DR-022-R1",
                text: "Deviation captured within 24 hours of detection.",
                criticality: "Critical",
            },
            {
                id: "DR-022-R2",
                text: "CAPA linked to the originating change control (see SOP-CC-005).",
                criticality: "Major",
            },
            {
                id: "DR-022-R3",
                text: "Root cause documented; preventive action tracked to closure.",
                criticality: "Major",
            },
        ],
    },
];

// Directed edges (from → to) with a risk state. `from_rule` / `to_rule`
// let us draw the cross-SOP line into the specific rule inside the target
// card (the hero visual of Direction C).
export const LINKS = [
    {
        id: "L1",
        from: "SOP-DI-004",
        from_rule: "DI-004-R2",
        to: "SOP-CSV-001",
        to_rule: "CSV-001-R1",
        risk: "aligned",
        note: "Data Integrity cites CSV for access control rigor. Both current, consistent.",
    },
    {
        id: "L2",
        from: "SOP-CC-007",
        from_rule: "CC-007-R1",
        to: "SOP-CSV-001",
        to_rule: "CSV-001-R4",
        risk: "contradicts",
        note: "Change Control § R1 exempts 'minor' changes — CSV § R4 requires controls for ALL changes. Inspector will flag.",
    },
    {
        id: "L3",
        from: "SOP-AT-012",
        from_rule: "AT-012-R1",
        to: "SOP-DI-004",
        to_rule: "DI-004-R3",
        risk: "stale",
        note: "Audit Trail Review SOP says WEEKLY; Data Integrity current revision says QUARTERLY. AT-012 last reviewed Feb 2023 — never updated when DI-004 moved to v3.0.",
    },
    {
        id: "L4",
        from: "SOP-DR-022",
        from_rule: "DR-022-R2",
        to: "SOP-CC-007",
        to_rule: null, // references SOP-CC-005 which doesn't exist
        risk: "broken",
        note: "Deviation Response cites SOP-CC-005 in R2 — that SOP was deprecated in 2024 and replaced by SOP-CC-007. Reference was never updated.",
    },
    {
        id: "L5",
        from: "SOP-DI-004",
        from_rule: "DI-004-R4",
        to: "SOP-CSV-001",
        to_rule: "CSV-001-R2",
        risk: "drift",
        note: "Both SOPs talk about e-signature posture but use different wording — 'compliant with 11.200' vs 'signed by QA Manager'. Same intent, words drifted apart.",
    },
    {
        id: "L6",
        from: "SOP-CC-007",
        from_rule: "CC-007-R3",
        to: "SOP-DR-022",
        to_rule: "DR-022-R1",
        risk: "aligned",
        note: "CAPA trigger in Change Control correctly routes to Deviation Response.",
    },
];

// Order of severity for triage sort
export const RISK_WEIGHT = {
    contradicts: 4,
    broken: 3,
    stale: 2,
    drift: 1,
    aligned: 0,
};

// Readiness score — entirely cosmetic, calibrated so the default
// center (DI-004) lights up a believable "76%" posture.
export const READINESS = {
    score: 76,
    broken_links: LINKS.filter((l) => l.risk !== "aligned").length,
    critical_breaks: LINKS.filter((l) => l.risk === "contradicts").length,
    stale_refs: LINKS.filter((l) => l.risk === "stale").length,
    broken_refs: LINKS.filter((l) => l.risk === "broken").length,
    systems_affected: 4, // number of distinct DMS silos touched
    sops_at_risk: new Set(
        LINKS.filter((l) => l.risk !== "aligned").flatMap((l) => [l.from, l.to]),
    ).size,
};
